import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import OutboundIntegrationLogDto from '@/models/Integrations/OutboundIntegrationLogDto';
import OutboundIntegrationLogListItemDto from '@/models/Integrations/OutboundIntegrationLogListItemDto';

export interface OutboundIntegrationLogListFilters {
    integrationId: string;
    limit: number;
    offset: number;
    sortBy: string;    // 'requestTime' | 'durationMs' | 'httpResponseCode'
    sortOrder: string; // 'asc' | 'desc'
}

export interface IOutboundIntegrationLogsState {
    loading: boolean;
    items: OutboundIntegrationLogListItemDto[];
    total: number;
    filters: OutboundIntegrationLogListFilters;
    selected: OutboundIntegrationLogDto | null;
    selectedLoading: boolean;
}

function defaultFilters(): OutboundIntegrationLogListFilters {
    return {
        integrationId: '',
        limit: 25,
        offset: 0,
        sortBy: 'requestTime',
        sortOrder: 'desc',
    };
}

@Module({ dynamic: true, store, name: 'OutboundIntegrationLogs', namespaced: true })
class OutboundIntegrationLogs extends VuexModule implements IOutboundIntegrationLogsState {
    public loading = false;
    public items: OutboundIntegrationLogListItemDto[] = [];
    public total = 0;
    public filters: OutboundIntegrationLogListFilters = defaultFilters();
    public selected: OutboundIntegrationLogDto | null = null;
    public selectedLoading = false;

    @Mutation
    setLoading(v: boolean) { this.loading = v; }

    @Mutation
    setSelectedLoading(v: boolean) { this.selectedLoading = v; }

    @Mutation
    setItems(payload: { items: OutboundIntegrationLogListItemDto[]; total: number }) {
        this.items = payload.items;
        this.total = payload.total;
    }

    @Mutation
    setFilters(f: OutboundIntegrationLogListFilters) { this.filters = f; }

    @Mutation
    setSelected(entry: OutboundIntegrationLogDto | null) { this.selected = entry; }

    @Action
    async setFiltersAndFetch(f: OutboundIntegrationLogListFilters) {
        this.setFilters(f);
        await this.fetch();
    }

    @Action
    async fetch() {
        if (!this.filters.integrationId) {
            this.setItems({ items: [], total: 0 });
            return;
        }
        this.setLoading(true);
        try {
            const qs = [
                `limit=${this.filters.limit}`,
                `offset=${this.filters.offset}`,
                `sortBy=${encodeURIComponent(this.filters.sortBy)}`,
                `sortOrder=${encodeURIComponent(this.filters.sortOrder)}`,
            ].join('&');
            const response = await ajax.get(`/api/v1/agency/integrations/${this.filters.integrationId}/outbound-logs?${qs}`);
            const data = response.data || {};
            this.setItems({ items: data.items || [], total: data.total || 0 });
        } catch (error) {
            AppModule.handleApiError(error);
            this.setItems({ items: [], total: 0 });
        } finally {
            this.setLoading(false);
        }
    }

    @Action
    async fetchById(payload: { integrationId: string; logId: string }) {
        this.setSelectedLoading(true);
        this.setSelected(null);
        try {
            const response = await ajax.get(`/api/v1/agency/integrations/${payload.integrationId}/outbound-logs/${payload.logId}`);
            this.setSelected(response.data as OutboundIntegrationLogDto);
        } catch (error) {
            AppModule.handleApiError(error);
            this.setSelected(null);
        } finally {
            this.setSelectedLoading(false);
        }
    }

    @Action
    clearSelected() {
        this.setSelected(null);
    }

    @Action
    cleanState() {
        this.context.commit('resetState');
    }

    @Mutation
    resetState() {
        this.loading = false;
        this.items = [];
        this.total = 0;
        this.filters = defaultFilters();
        this.selected = null;
        this.selectedLoading = false;
    }
}

export const OutboundIntegrationLogsModule = getModule(OutboundIntegrationLogs);
